import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  AutosuggestProps,
  AutosuggestSearchItem,
  HomeVideoReviewSearchItem
} from "@bluframe/mightychroma-mightytool-frontend-types"
import {
  useCombobox,
  UseComboboxGetInputPropsReturnValue,
  UseComboboxInputValueChange,
  UseComboboxPropGetters
} from "downshift"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import Autosuggest from "./Autosuggest"

export interface Props extends AutosuggestProps {}

export interface ComponentProps extends Omit<
  Props,
  "onGetSuggestions" | "suggestions"
> {
  getItemProps: UseComboboxPropGetters<AutosuggestSearchItem>["getItemProps"]
  getMenuProps: UseComboboxPropGetters<AutosuggestSearchItem>["getMenuProps"]
  inputProps: UseComboboxGetInputPropsReturnValue & {
    ref: React.RefObject<HTMLInputElement | null>
  }
  isOpen: boolean
  suggestions: AutosuggestSearchItem[]
}

const DEFAULT_DEBOUNCE_DELAY = 500

export const usePrepareComponent = ({
  inputId = "search",
  onGetSuggestions,
  onSuggestionClick: onSuggestionClickProp,
  suggestions = [],
  ...props
}: Props): ComponentProps => {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const lastValueRef = useRef<string | undefined>(props.value)

  // Debounced search: only fire on user typing, not on selection
  const debouncedSearch = useCallback(
    (changes: UseComboboxInputValueChange<AutosuggestSearchItem>) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      const newTimeoutId = setTimeout(() => {
        onGetSuggestions(changes)
      }, DEFAULT_DEBOUNCE_DELAY)
      setTimeoutId(newTimeoutId)
    },
    [timeoutId, onGetSuggestions]
  )

  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    isOpen,
    reset,
    selectItem,
    selectedItem
  } = useCombobox<AutosuggestSearchItem>({
    inputId,
    itemToKey: (item) => item?.id,
    itemToString: (item) =>
      (item?.name || (item as HomeVideoReviewSearchItem)?.mas?.title) ?? "",
    items: suggestions,
    labelId: `${inputId}-label`,
    onInputValueChange: (inputValueChange) => {
      // Only trigger search on user typing ('__input_change__'), not on selection ('__mouse_up__')
      if (inputValueChange?.type === useCombobox.stateChangeTypes.InputChange) {
        debouncedSearch(inputValueChange)
      }
    }
  })

  const inputProps: UseComboboxGetInputPropsReturnValue & {
    ref: React.RefObject<HTMLInputElement | null>
  } = getInputProps()

  // 1) Clear when parent sets value=""
  useEffect(() => {
    if (props.value !== lastValueRef.current) {
      // just transitioned
      if (props.value === "" || props.value === undefined) {
        // clear selection + input only on actual transition-to-empty
        selectItem(null)
        reset()
      }
      lastValueRef.current = props.value
    }
  }, [props.value, reset, selectItem])
  // 2) Sync selection when a concrete id is provided
  useEffect(() => {
    if (!props.value || props.value === "") return

    const selected = suggestions.find((item) => item.id === props.value)
    if (selected && selectedItem?.id !== selected.id) {
      selectItem(selected)
    }
  }, [props.value, suggestions, selectItem, selectedItem?.id])

  const onSuggestionClick = useCallback(
    (id: string, name: string) => {
      onSuggestionClickProp(id, name)
    },
    [onSuggestionClickProp]
  )

  return {
    ...props,
    getItemProps,
    getMenuProps,
    inputProps,
    isOpen,
    onSuggestionClick,
    suggestions
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Autosuggest
)
